import { useCallback } from 'react';

import { Box, ButtonBase, Skeleton } from '@mui/material';

import CheckboxMultipleOutline from '@/assets/icons/CheckboxMultipleOutline';
import Typography from '@/components/Typography';
import { symbolKey } from '@/entities/symbol';
import type { PastExportDetails } from '@/features/DataHub/api/pastExports.api';
import { ExportDetails } from '@/features/DataHub/components/ExportDetails';
import { ItemDetails } from '@/features/DataHub/components/ItemDetails';
import { isSourceBackedDataExport } from '@/features/DataHub/utils/isSourceBackedDataExport';
import {
  clearSelection,
  requestForwardCurveSelection,
  requestSpotSelection
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';

export const ExportItemDetails = ({
  isLoading,
  data,
  optionLabel,
  expanded,
  onExpandedChange
}: {
  isLoading: boolean;
  data?: PastExportDetails;
  optionLabel?: string | null;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}) => {
  const dispatch = useAppDispatch();

  const isSourceBackedDataItem = isSourceBackedDataExport(data);
  const isForwardCurveExport =
    (data?.additionalParams as { fwdCurve?: boolean } | undefined)?.fwdCurve === true;

  const handleClick = useCallback(() => {
    const symbols = data?.symbols ?? [];
    dispatch(clearSelection());
    if (isForwardCurveExport) {
      dispatch(requestForwardCurveSelection({ symbols }));
    } else {
      dispatch(requestSpotSelection({ symbols }));
    }
    dispatch(closeDrawer());
  }, [data?.symbols, dispatch, isForwardCurveExport]);
  const symbols = data?.symbols ?? [];
  const symbolCount = isForwardCurveExport
    ? new Set(symbols.map(s => symbolKey(s, true))).size
    : symbols.length;
  const plural = symbolCount > 1 ? 's' : '';
  const countLabel = isForwardCurveExport
    ? `${symbolCount} curve${plural}`
    : `${symbolCount} item${plural}`;

  return (
    <>
      {isLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1.5 }}>
          <Skeleton
            height='14px'
            width='35%'
          />
          <Skeleton
            height='14px'
            width='55%'
          />
          <Skeleton
            height='14px'
            width='55%'
          />
          <Skeleton height='14px' />
          <Skeleton height='14px' />
        </Box>
      )}
      {!isLoading && data && (
        <>
          {!isSourceBackedDataItem && data.symbols.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckboxMultipleOutline
                    sx={theme => ({ fontSize: theme.typography.pxToRem(16) })}
                  />
                  <Typography size={16}>{countLabel}</Typography>
                </Box>
                <ButtonBase
                  sx={{
                    fontSize: '14px',
                    display: 'flex',
                    gap: 1,
                    backgroundColor: 'gx.actionBarButtonTile',
                    px: '16px',
                    py: '2px'
                  }}
                  onClick={handleClick}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: 'primary.main'
                    }}
                  />
                  Add to selector
                </ButtonBase>
              </Box>

              <ItemDetails
                expanded={expanded}
                isForwardCurve={isForwardCurveExport}
                readOnly
                symbols={data.symbols}
                onExpandedChange={onExpandedChange}
              />
            </>
          )}
          <ExportDetails
            exportTimestamp={data.exportTimestamp}
            hideNoMetadataLabel={isSourceBackedDataItem}
            name={optionLabel ?? undefined}
            settings={{
              date: {
                from: data.from,
                to: data.to
              },
              timezone: data.timezone,
              includeMetadata: data.metadata === 'true'
            }}
          />
        </>
      )}
      {!isLoading && !data && <Typography>Failed to load data</Typography>}
    </>
  );
};
